import { Camera } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { type AuthUser, useAuthStore } from "@/features/auth/store/auth-store";
import { useChatStore } from "@/features/chat/store/chat-store";
import { initials } from "@/features/chat/utils";
import { type SettingsUser, uploadAvatar } from "@/features/settings/api";
import { ApiError } from "@/lib/api";
import { useAuthedImage } from "@/hooks/use-authed-image";
import { cn } from "@/lib/utils";

/** The pill label shown next to the name — platform scope wins over org role. */
function roleLabel(user: AuthUser | null): string | null {
  if (!user) return null;
  if (user.is_superuser || user.is_platform_admin) return "Global Admin";
  switch (user.role) {
    case "country_director":
      return "Country Director";
    case "team_leader":
      return "Team Leader";
    case "staff":
      return "Staff";
    default:
      return user.account_type === "individual" ? "Account Owner" : null;
  }
}

interface IdentityHeroProps {
  user: SettingsUser;
  /** Called with the refreshed user after a successful avatar upload. */
  onAvatarUploaded: (user: SettingsUser) => void;
}

/** The navy identity banner at the top of /profile — avatar (with upload), name,
 * role badge, context line, and a strip of personal stats. */
export function IdentityHero({ user, onAvatarUploaded }: IdentityHeroProps) {
  const authUser = useAuthStore((s) => s.user);
  const unread = useChatStore((s) => s.unreadTotal);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const avatarSrc = useAuthedImage(
    user.avatarUrl ? `/auth/me/avatar?v=${encodeURIComponent(user.avatarUrl)}` : null,
  );
  const badge = roleLabel(authUser);
  const firmName = authUser?.firm?.name;
  const memberSince = user.memberSince
    ? new Date(user.memberSince).toLocaleDateString(undefined, {
        month: "short",
        year: "numeric",
      })
    : null;

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setUploading(true);
    try {
      const updated = await uploadAvatar(file);
      onAvatarUploaded(updated);
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't upload photo.");
    } finally {
      setUploading(false);
    }
  }

  const stats: { label: string; value: string; accent?: boolean }[] = [
    { label: "Your clients", value: "—" },
    { label: "Open tasks", value: "—" },
    { label: "Unread chats", value: String(unread), accent: unread > 0 },
    { label: "Last sign-in", value: "Today" },
  ];

  return (
    <Card id="identity" className="scroll-mt-24 gap-0 overflow-hidden p-0">
      {/* Navy band with soft decorative glows */}
      <div className="bg-brand-panel relative h-24 overflow-hidden">
        <div
          aria-hidden
          className="bg-primary/30 absolute -top-24 -right-16 size-64 rounded-full blur-2xl"
        />
        <div
          aria-hidden
          className="absolute top-5 left-[30%] size-36 -rotate-[30deg] rounded-full border border-l-transparent border-white/10"
        />
      </div>

      {/* Avatar + identity row (overlaps the band) */}
      <div className="relative -mt-10 flex flex-wrap items-end gap-5 px-6 pb-6">
        <div className="relative shrink-0">
          <div className="bg-card shadow-card size-24 rounded-[24px] p-1">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt=""
                className="size-full rounded-[20px] object-cover"
              />
            ) : (
              <div className="bg-accent text-info font-heading flex size-full items-center justify-center rounded-[20px] text-3xl font-bold tracking-tight">
                {initials(user.name)}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            aria-label="Change photo"
            className="border-border bg-card text-info shadow-card absolute -right-1.5 -bottom-1.5 flex size-9 items-center justify-center rounded-full border disabled:opacity-60"
          >
            <Camera className="size-[15px]" />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={handleFile}
          />
        </div>

        <div className="flex min-w-0 flex-1 basis-70 flex-col gap-1 pb-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="font-heading text-2xl font-semibold tracking-tight">
              {user.name}
            </span>
            {badge && (
              <span className="bg-brand-panel rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wider text-white uppercase">
                {badge}
              </span>
            )}
          </div>
          <span className="text-muted-foreground text-[13px]">
            {user.email}
            {firmName && <> · {firmName} workspace</>}
            {memberSince && (
              <>
                {" "}
                · member since <span className="tabular-nums">{memberSince}</span>
              </>
            )}
          </span>
        </div>
      </div>

      {/* Personal stats strip */}
      <div className="grid grid-cols-2 border-t sm:grid-cols-4">
        {stats.map((s, i) => (
          <div
            key={s.label}
            className={cn(
              "flex flex-col gap-0.5 px-6 py-3.5",
              i > 1 && "border-t sm:border-t-0",
              i % 2 === 0 && "border-r",
              i < stats.length - 1 && "sm:border-r",
            )}
          >
            <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
              {s.label}
            </span>
            <span
              className={cn(
                "font-heading text-xl font-semibold tabular-nums",
                s.accent && "text-warning",
              )}
            >
              {s.value}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}
